import { headingStructureRule, RuleId } from '@wix/a11y-rules';
import { useErrorHandler, useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { ElementStatus, makeHeadingElementKey, RuleListStatus, useHeadingStructureSuggestions } from '@wix/rules-panel-ui';
import { produce } from 'immer';
import { useAtom, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import { cancelHeadingStructureAIAtom, headingStructureAIAtomFamily, headingStructureAILoadingPageIdAtom, headingStructureAIPagesWithResultAtom } from '../../../../atoms/heading-structure-ai-atoms';
import { isApplyTriggeredAtom, ruleApplyTriggeredAtom } from '../../../../atoms/session-atoms';
import { BiCtaNames, BiNotificationTypes } from '../../../../bi/types';
import { headingStructureOnApply } from '../../../../rule-mappers/page-level-rules/heading-structure-on-apply';
import { useGlobalSectionState } from '../../../../rule-mappers/shared/use-global-section-state';
import { useShowHeadingStructureAIErrorModal } from '../../../modals/show-heading-structure-ai-error-modal';
import { showStopAIHeadingStructureModal } from '../../../modals/show-stop-ai-heading-structure-modal';
import { convertScanStatusToUiStatus } from '../../alt-image-fixer/utils';
import { findHtmlString, findHtmlStringIndex } from '../utils/find-html-string-index';
const ruleId = RuleId.HEADING_STRUCTURE;
const ruleChecker = headingStructureRule.checker;
export const useHeadingStructureAI = _ref => {
  let {
    pageId,
    itemType,
    htmlStrings,
    headings,
    memoizedHeadingProps,
    a11yWizardAdapter,
    editorHost,
    getRuleState,
    updateRuleStateAndVerifyIfNeeded,
    biLogger
  } = _ref;
  const {
    experiments
  } = useExperiments();
  const {
    t
  } = useTranslation();
  const errorHandler = useErrorHandler();
  const {
    getHeadingStructureSuggestions
  } = useHeadingStructureSuggestions(errorHandler);
  const {
    updateGlobalSectionStateIfNeeded,
    getGlobalSectionModifiedComponentIds
  } = useGlobalSectionState();
  const [aiState, setAIState] = useAtom(headingStructureAIAtomFamily(pageId));
  const cancelHeadingStructureAI = useSetAtom(cancelHeadingStructureAIAtom);
  const showHeadingStructureAIErrorModal = useShowHeadingStructureAIErrorModal();
  const setPagesWithResult = useSetAtom(headingStructureAIPagesWithResultAtom);
  const setRuleApplyTriggered = useSetAtom(ruleApplyTriggeredAtom);
  const setIsApplyTriggered = useSetAtom(isApplyTriggeredAtom);
  const {
    isLoading: isAILoading,
    result: aiResult,
    generatedElements,
    hasAppliedAiFixes
  } = aiState;
  const findPropIndex = useCallback((id, attributeId, uiElementId) => {
    // Normalize null, undefined, and "" to null so backend empty-string echoes
    // match props that store null (and vice versa).
    const normalizeAttrId = v => v || null;
    return memoizedHeadingProps.findIndex(el => el.id === id && normalizeAttrId(el.attributeId) === normalizeAttrId(attributeId) && el.uiElementId === uiElementId);
  }, [memoizedHeadingProps]);
  const applySingleHeadingChange = useCallback(async (newTag, el, currentHtmlStrings) => {
    const newHeading = await headingStructureOnApply(newTag, el.headingDefinition, el.htmlString, el.id, el.uiElementId, a11yWizardAdapter, editorHost, experiments.enabled('specs.seo.fixHeadingStructureWithAi'), el.attributeId);
    const oldIdx = findHtmlStringIndex(currentHtmlStrings, newHeading);
    if (oldIdx === -1) {
      return currentHtmlStrings;
    }
    return produce(currentHtmlStrings, draft => {
      draft[oldIdx] = newHeading;
    });
  }, [a11yWizardAdapter, editorHost, experiments]);

  // Phase A: pure derivation — turns AI suggestions into a map of headings the
  // user might fix. Runs at fetch time so the AiNotification preview, generic
  // sparkles, and the "AI suggestion" blue box all light up before Apply.
  const buildGeneratedMap = useCallback(suggestions => {
    const generatedMap = new Map();
    for (const suggestion of suggestions ?? []) {
      var _headings$propIndex;
      if (!suggestion.proposedTag || !suggestion.uiElementId) {
        continue;
      }
      const propIndex = findPropIndex(suggestion.id ?? '', suggestion.attributeId, suggestion.uiElementId);
      if (propIndex === -1) {
        continue;
      }
      const isGeneric = (_headings$propIndex = headings[propIndex]) == null ? void 0 : _headings$propIndex.isGeneric;
      generatedMap.set(makeHeadingElementKey(suggestion.id ?? '', suggestion.attributeId, suggestion.uiElementId), {
        attributeId: suggestion.attributeId,
        id: suggestion.id ?? '',
        isGeneric,
        proposedTag: suggestion.proposedTag,
        uiElementId: suggestion.uiElementId
      });
    }
    return generatedMap;
  }, [findPropIndex, headings]);

  // Writes a target tag (resolved per entry by `getTargetTag`) to the editor
  // for every non-generic entry in the map and re-verifies the rule. Used by
  // both Apply (proposed tag from AI) and Undo (original tag from snapshot).
  const applyNonGenericTagChanges = useCallback(async (generatedMap, getTargetTag, _ref2) => {
    var _getRuleState;
    let {
      triggerRuleNavigation
    } = _ref2;
    if (generatedMap.size === 0) {
      return;
    }
    const changedComponentIds = new Set();
    const ruleState = (_getRuleState = getRuleState(pageId, ruleId)) == null ? void 0 : _getRuleState.state;
    let currentHtmlStrings = [...((ruleState == null ? void 0 : ruleState.htmlStrings) ?? htmlStrings)];
    for (const entry of generatedMap.values()) {
      if (entry.isGeneric) {
        continue;
      }
      const propIndex = findPropIndex(entry.id, entry.attributeId, entry.uiElementId);
      if (propIndex === -1) {
        continue;
      }
      const targetTag = getTargetTag(entry);
      if (!targetTag) {
        continue;
      }
      const el = memoizedHeadingProps[propIndex];
      try {
        currentHtmlStrings = await applySingleHeadingChange(targetTag, el, currentHtmlStrings);
        changedComponentIds.add(el.id);
      } catch (err) {
        console.error('AI tag change failed for', entry.uiElementId, err);
        continue;
      }
    }
    const globalSectionInputMap = new Map();
    for (const {
      id,
      uiElementId,
      attributeId
    } of generatedMap.values()) {
      if (!changedComponentIds.has(id)) {
        continue;
      }
      const updated = findHtmlString(currentHtmlStrings, {
        attributeId,
        id,
        uiElementId
      });
      if (updated) {
        globalSectionInputMap.set(id, {
          componentId: id,
          htmlString: updated.htmlString
        });
      }
    }
    const updatedGlobalSectionDataMap = updateGlobalSectionStateIfNeeded(globalSectionInputMap);
    const modifiedCompIds = getGlobalSectionModifiedComponentIds(updatedGlobalSectionDataMap);
    updateRuleStateAndVerifyIfNeeded(pageId, ruleId, {
      htmlStrings: currentHtmlStrings,
      status: convertScanStatusToUiStatus(ruleChecker({
        htmlStrings: currentHtmlStrings
      }).status)
    }, modifiedCompIds);
    if (triggerRuleNavigation) {
      const allNonGeneric = [...generatedMap.values()].every(el => !el.isGeneric);
      if (allNonGeneric) {
        setIsApplyTriggered(true);
        setRuleApplyTriggered({
          pageId,
          ruleId
        });
      }
    }
  }, [applySingleHeadingChange, findPropIndex, getGlobalSectionModifiedComponentIds, getRuleState, htmlStrings, memoizedHeadingProps, pageId, setIsApplyTriggered, setRuleApplyTriggered, updateGlobalSectionStateIfNeeded, updateRuleStateAndVerifyIfNeeded]);
  const applyNonGenericFixes = useCallback(generatedMap => applyNonGenericTagChanges(generatedMap, entry => entry.proposedTag, {
    triggerRuleNavigation: true
  }), [applyNonGenericTagChanges]);
  const buildHeadingAiPayload = useCallback((map, isApplied) => {
    const entries = [...map.values()];
    const nonGeneric = entries.filter(entry => !entry.isGeneric).length;
    return {
      genericSuggestion: entries.length - nonGeneric,
      isApplied,
      nonGenericSuggestion: nonGeneric
    };
  }, []);
  const onApplyAiFixes = useAtomCallback(useCallback(async (get, set) => {
    const aiAtom = headingStructureAIAtomFamily(pageId);
    const map = get(aiAtom).generatedElements;
    biLogger.taskNotificationCtaClick({
      ctaName: BiCtaNames.HeadingAiApply,
      notificationType: BiNotificationTypes.headingStructureAiNotificationView,
      payload: buildHeadingAiPayload(map, false)
    });
    await applyNonGenericFixes(map);
    set(aiAtom, s => ({
      ...s,
      hasAppliedAiFixes: true
    }));
  }, [applyNonGenericFixes, biLogger, buildHeadingAiPayload, pageId]));
  const clearAiResult = useCallback(() => {
    setAIState(s => ({
      ...s,
      generatedElements: new Map(),
      hasAppliedAiFixes: false,
      result: null,
      snapshot: null
    }));
    setPagesWithResult(prev => {
      const next = new Set(prev);
      next.delete(pageId);
      return next;
    });
  }, [pageId, setAIState, setPagesWithResult]);
  const onDismissResult = useAtomCallback(useCallback(get => {
    const aiAtom = headingStructureAIAtomFamily(pageId);
    const map = get(aiAtom).generatedElements;
    biLogger.taskNotificationCtaClick({
      ctaName: BiCtaNames.HeadingAiDismiss,
      notificationType: BiNotificationTypes.AiCanMakeMistakes,
      payload: buildHeadingAiPayload(map, false)
    });
    clearAiResult();
  }, [biLogger, buildHeadingAiPayload, clearAiResult, pageId]));
  const revertNonGenericFixes = useCallback((generatedMap, snapshot) => {
    if (!snapshot) {
      return Promise.resolve();
    }
    return applyNonGenericTagChanges(generatedMap, entry => {
      var _original$htmlString$;
      const original = findHtmlString(snapshot, {
        attributeId: entry.attributeId,
        id: entry.id,
        uiElementId: entry.uiElementId
      });
      return original == null || (_original$htmlString$ = original.htmlString.match(/<\s*(h[1-6])/i)) == null || (_original$htmlString$ = _original$htmlString$[1]) == null ? void 0 : _original$htmlString$.toLowerCase();
    }, {
      triggerRuleNavigation: false
    });
  }, [applyNonGenericTagChanges]);
  const onUndo = useAtomCallback(useCallback(async get => {
    const aiAtom = headingStructureAIAtomFamily(pageId);
    const {
      hasAppliedAiFixes: applied,
      generatedElements: map,
      snapshot
    } = get(aiAtom);
    biLogger.taskNotificationCtaClick({
      ctaName: BiCtaNames.HeadingAiUndo,
      notificationType: BiNotificationTypes.headingStructureAiNotificationView,
      payload: buildHeadingAiPayload(map, applied)
    });
    if (applied) {
      await revertNonGenericFixes(map, snapshot);
    }
    clearAiResult();
  }, [biLogger, buildHeadingAiPayload, clearAiResult, pageId, revertNonGenericFixes]));
  const onShowMore = useAtomCallback(useCallback(get => {
    const aiAtom = headingStructureAIAtomFamily(pageId);
    const {
      hasAppliedAiFixes: applied,
      generatedElements: map
    } = get(aiAtom);
    biLogger.taskNotificationCtaClick({
      ctaName: BiCtaNames.HeadingAiShowMore,
      notificationType: BiNotificationTypes.headingStructureAiNotificationView,
      payload: buildHeadingAiPayload(map, applied)
    });
  }, [biLogger, buildHeadingAiPayload, pageId]));
  const onAIFix = useAtomCallback(useCallback(async (get, set) => {
    var _getRuleState2;
    biLogger.taskCtaClick({
      ctaName: BiCtaNames.HeadingAiFix,
      elementCount: memoizedHeadingProps.length,
      failedElementCount: memoizedHeadingProps.filter(h => h.status === ElementStatus.Failed).length
    });
    const aiAtom = headingStructureAIAtomFamily(pageId);
    const ruleHtmlStrings = ((_getRuleState2 = getRuleState(pageId, ruleId)) == null || (_getRuleState2 = _getRuleState2.state) == null ? void 0 : _getRuleState2.htmlStrings) ?? htmlStrings;
    set(aiAtom, s => ({
      ...s,
      generatedElements: new Map(),
      hasAppliedAiFixes: false,
      isCancelled: false,
      isLoading: true,
      snapshot: [...ruleHtmlStrings]
    }));
    set(headingStructureAILoadingPageIdAtom, pageId);
    try {
      const result = await getHeadingStructureSuggestions(headings, pageId, itemType ?? '');
      if (get(aiAtom).isCancelled) {
        return;
      }
      const generatedMap = buildGeneratedMap(result == null ? void 0 : result.suggestions);
      set(aiAtom, s => ({
        ...s,
        generatedElements: generatedMap,
        result
      }));
      if (generatedMap.size > 0) {
        set(headingStructureAIPagesWithResultAtom, pages => {
          const next = new Set(pages);
          next.add(pageId);
          return next;
        });
      }
    } catch (_err) {
      if (get(aiAtom).isCancelled) {
        return;
      }
      showHeadingStructureAIErrorModal({
        handleCancel: () => {},
        handleTryAgain: () => onAIFix()
      });
    } finally {
      set(aiAtom, s => ({
        ...s,
        isLoading: false
      }));
      set(headingStructureAILoadingPageIdAtom, null);
    }
  },
  // `onAIFix` is intentionally omitted: including it would create a circular
  // dependency (onAIFix → useCallback deps → onAIFix). It is safe to omit
  // because onAIFix is stable across renders and the retry callback only
  // fires after the current invocation has already resolved.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [a11yWizardAdapter, biLogger, buildGeneratedMap, editorHost, getHeadingStructureSuggestions, getRuleState, headings, htmlStrings, itemType, memoizedHeadingProps, pageId, showHeadingStructureAIErrorModal, t]));
  const onCancel = useCallback(() => {
    showStopAIHeadingStructureModal({
      a11yWizardAdapter,
      editorHost,
      handleContinue: () => {},
      handleStop: () => {
        biLogger.ctaClick({
          ctaName: BiCtaNames.HeadingAiStop
        });
        cancelHeadingStructureAI(pageId);
      },
      t
    });
  }, [a11yWizardAdapter, biLogger, cancelHeadingStructureAI, editorHost, pageId, t]);
  const showFixWithAiButton = useMemo(() => experiments.enabled('specs.seo.fixHeadingStructureWithAi'), [experiments]);
  const onUserOverrideTag = useAtomCallback(useCallback((get, set, elementIndex) => {
    var _getRuleState3;
    const el = memoizedHeadingProps[elementIndex];
    if (!el) {
      return;
    }
    const key = makeHeadingElementKey(el.id, el.attributeId, el.uiElementId);
    const aiAtom = headingStructureAIAtomFamily(pageId);
    const current = get(aiAtom);
    if (!current.generatedElements.has(key)) {
      return;
    }
    const ruleFailedAgain = ((_getRuleState3 = getRuleState(pageId, ruleId)) == null || (_getRuleState3 = _getRuleState3.state) == null ? void 0 : _getRuleState3.status) === RuleListStatus.failed;
    if (current.hasAppliedAiFixes && ruleFailedAgain) {
      set(aiAtom, s => ({
        ...s,
        generatedElements: new Map(),
        hasAppliedAiFixes: false,
        result: null,
        snapshot: null
      }));
      set(headingStructureAIPagesWithResultAtom, prev => {
        const next = new Set(prev);
        next.delete(pageId);
        return next;
      });
      return;
    }
    const updated = new Map(current.generatedElements);
    updated.delete(key);
    set(aiAtom, s => ({
      ...s,
      generatedElements: updated
    }));
    if (updated.size === 0) {
      set(headingStructureAIPagesWithResultAtom, prev => {
        const next = new Set(prev);
        next.delete(pageId);
        return next;
      });
    }
  }, [getRuleState, memoizedHeadingProps, pageId]));
  const effectiveAiResult = useMemo(() => {
    var _aiResult$suggestions;
    if (!aiResult) {
      return null;
    }
    return {
      ...aiResult,
      suggestions: (_aiResult$suggestions = aiResult.suggestions) == null ? void 0 : _aiResult$suggestions.filter(s => generatedElements.has(makeHeadingElementKey(s.id ?? '', s.attributeId, s.uiElementId ?? '')))
    };
  }, [aiResult, generatedElements]);
  const onAiNotificationView = useCallback(_ref3 => {
    let {
      nonGenericCount,
      genericCount,
      isApplied
    } = _ref3;
    biLogger.taskNotificationView({
      notificationType: BiNotificationTypes.headingStructureAiNotificationView,
      payload: {
        genericSuggestion: genericCount,
        isApplied,
        nonGenericSuggestion: nonGenericCount
      }
    });
  }, [biLogger]);
  const aiProviderValue = useMemo(() => ({
    actions: {
      onAIFix,
      onAiNotificationView,
      onApplyAiFixes,
      onCancel,
      onDismissResult,
      onShowMore,
      onUndo
    },
    showFixWithAiButton,
    state: {
      aiResult: effectiveAiResult,
      hasAppliedAiFixes,
      isAILoading
    }
  }), [onAIFix, onAiNotificationView, onApplyAiFixes, onCancel, onDismissResult, onShowMore, onUndo, showFixWithAiButton, effectiveAiResult, hasAppliedAiFixes, isAILoading]);
  return {
    aiProviderValue,
    onUserOverrideTag
  };
};
//# sourceMappingURL=use-heading-structure-ai.js.map